import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import styles from "../../../../pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import { CustomButton, CustomDatePicker } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import dayjs from "dayjs";

const AssessmentList = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels || {};

  const user = TokenService.getUser();
  const userId = user?.id || "";
  const clientData = user?.client_data?.[0] || {};
  const schoolId =
    clientData.school_id != null && clientData.school_id !== ""
      ? Number(clientData.school_id)
      : null;
  const academic_id = clientData?.academic_year_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const [columnFilters, setColumnFilters] = useState([]);

  const [titleSearch, setTitleSearch] = useState("");
  const [topicSearch, setTopicSearch] = useState("");
  const [subTopicSearch, setSubTopicSearch] = useState("");
  const [subjectSearch, setSubjectSearch] = useState("");
  const [classSearch, setClassSearch] = useState("");
  const [sectionSearch, setSectionSearch] = useState("");
  const [mediumSearch, setMediumSearch] = useState("");
  const [createdDateSearch, setCreatedDateSearch] = useState("");

  // ==================== DELETE FEATURE STATE ====================
  const [selectedId, setSelectedId] = useState(null);
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
  });

  // Map of column id -> setter (used to sync cleared filters back to
  // their search state).
  const filterSetters = useMemo(
    () => ({
      title: setTitleSearch,
      topic: setTopicSearch,
      sub_topic: setSubTopicSearch,
      subject: setSubjectSearch,
      class: setClassSearch,
      section_id: setSectionSearch,
      medium: setMediumSearch,
      created_at: setCreatedDateSearch,
    }),
    [],
  );

  // ==================== FETCH ====================
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        user_id: userId,
        school_id: schoolId,
        page: String(pagination.pageIndex + 1),
        limit: String(pagination.pageSize),
        title: titleSearch,
        topic: topicSearch,
        sub_topic: subTopicSearch,
        subject: subjectSearch,
        class_id: classSearch,
        section_id: sectionSearch,
        medium: mediumSearch,
        created_date: createdDateSearch,
        academic_id: academic_id,
      };

      const res = await AxiosInstance.post("/book/assessment/list", payload);
      if (res?.data?.status) {
        setData(res.data.data || []);
        setRowCount(res.data.pagination?.total || 0);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (err) {
      console.error("Assessment list error:", err);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    userId,
    schoolId,
    pagination.pageIndex,
    pagination.pageSize,
    titleSearch,
    topicSearch,
    subTopicSearch,
    subjectSearch,
    classSearch,
    sectionSearch,
    mediumSearch,
    createdDateSearch,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    setSelectedId(null);
  }, [data]);

  // AI key must exist in campus_ai_configuration (Settings → AI Key
  // Configuration) before the create page is opened; popup OK reloads.
  const handleAdd = async () => {
    try {
      const res = await AxiosInstance.get("/settings/ai/configuration");
      if (res.data?.status && res.data?.data) {
        navigate("/user/assessmentCreate");
        return;
      }
    } catch (err) {
      // treated as not configured
    }
    setModalState({
      open: true,
      type: "error",
      message:
        translations?.labels?.aiKeyNotConfigured ||
        "AI Key not configured. Please configure the AI Key in Settings.",
      aiKeyMissing: true,
    });
  };

  const handleView = (row) => {
    navigate(`/user/assessmentView`, {
      state: { id: row.id, data: row },
    });
  };

  // ==================== DELETE HANDLERS ====================
  const handleDeleteClick = () => {
    if (!selectedId) return;
    setModalState({
      open: true,
      type: "Confirm",
      message: t?.confirmDelete || "Are you sure you want to delete?",
    });
  };

  const handleConfirmDelete = async () => {
    try {
      const res = await AxiosInstance.post("/book/deleteAssessment", {
        id: selectedId,
        user_id: userId,
      });

      if (res?.data?.status === true) {
        setModalState({
          open: true,
          type: "success",
          message:
            res?.data?.message || t?.deleteSuccess || "Successfully deleted",
        });
        setSelectedId(null);
        fetchList();
      } else {
        setModalState({
          open: true,
          type: "failed",
          message: res?.data?.message || t?.deleteFailed || "Failed to delete",
        });
      }
    } catch (err) {
      console.error("Delete assessment error:", err);
      setModalState({
        open: true,
        type: "failed",
        message: t?.deleteFailed || "Failed to delete",
      });
    }
  };

  const handleModalClose = () => {
    // AI-key popup → reload the same page on close/OK
    if (modalState.aiKeyMissing) {
      window.location.reload();
      return;
    }
    setModalState({ open: false, type: "", message: "" });
  };

  // ==================== FILTER HANDLERS ====================
  const handleColumnFiltersChange = (updaterOrValue) => {
    const newFilters =
      typeof updaterOrValue === "function"
        ? updaterOrValue(columnFilters)
        : updaterOrValue;

    Object.keys(filterSetters).forEach((columnId) => {
      const oldFilter = columnFilters.find((f) => f.id === columnId);
      const newFilter = newFilters.find((f) => f.id === columnId);

      const oldValue = oldFilter?.value ?? "";
      const newValue = newFilter?.value ?? "";

      // Filter was cleared (had a value, now empty/removed)
      if (oldValue !== "" && newValue === "") {
        filterSetters[columnId]("");
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }
    });

    setColumnFilters(newFilters);
  };

  const makeFilterKeyDownHandler = (setter) => (e) => {
    if (e.key === "Enter") {
      const inputValue = e.target.value || "";
      setter(inputValue);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };

  // ==================== SINGLE DATE FILTER ====================
  const SingleDateFilter = ({ column, translations: t, onDateChange }) => {
    const filterValue = column.getFilterValue();
    const [localDate, setLocalDate] = useState(filterValue || "");

    useEffect(() => {
      setLocalDate(filterValue || "");
    }, [filterValue]);

    const dateValue = localDate ? dayjs(localDate) : null;

    const handleChange = (dayjsDate) => {
      const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
      setLocalDate(formatted);
      // Keep MRT's columnFilters in sync (so chips/badges update).
      column.setFilterValue(formatted || undefined);

      if (typeof onDateChange === "function") {
        onDateChange(formatted);
      }
    };

    return (
      <div className={styles.dateRangeFilter}>
        <CustomDatePicker
          name={`${column.id}_date`}
          value={dateValue}
          onChange={handleChange}
          placeholder={t?.labels?.createdDate || "Created Date"}
          format="DD/MM/YYYY"
          size="sm"
          isClearable={true}
          className={styles.dateRangePicker}
        />
      </div>
    );
  };

  const columns = useMemo(
    () => [
      // ==================== SELECT CHECKBOX COLUMN ====================
      {
        accessorKey: "select",
        header: "",
        enableColumnFilter: false,
        enableSorting: false,
        size: 50,
        Cell: ({ row }) => {
          const rowId = row.original.id;
          const isChecked = selectedId === rowId;
          return (
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() =>
                setSelectedId((prev) => (prev === rowId ? null : rowId))
              }
              className={styles.radioInput}
              aria-label="Select row"
            />
          );
        },
      },
      {
        accessorKey: "title",
        header: t.title || "Title",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: t.title || "Title",
          onKeyDown: makeFilterKeyDownHandler(setTitleSearch),
        },
      },
      {
        accessorKey: "topic",
        header: t.topic || "Topic",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: t.topic || "Topic",
          onKeyDown: makeFilterKeyDownHandler(setTopicSearch),
        },
      },
      {
        accessorKey: "sub_topic",
        header: t.subTopic || "Sub Topic",
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => cell.getValue() || "-",
        muiFilterTextFieldProps: {
          placeholder: t.subTopic || "Sub Topic",
          onKeyDown: makeFilterKeyDownHandler(setSubTopicSearch),
        },
      },
      {
        accessorKey: "subject",
        header: t.subject || "Subject",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: t.subject || "Subject",
          onKeyDown: makeFilterKeyDownHandler(setSubjectSearch),
        },
      },
      {
        accessorKey: "class",
        header: t.class || "Class",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: t.class || "Class",
          onKeyDown: makeFilterKeyDownHandler(setClassSearch),
        },
      },
      {
        accessorKey: "section_id",
        header: t.section || "Section",
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => cell.getValue() || "-",
        muiFilterTextFieldProps: {
          placeholder: t.section || "Section",
          onKeyDown: makeFilterKeyDownHandler(setSectionSearch),
        },
      },
      {
        accessorKey: "medium",
        header: t.medium || "Medium",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: t.medium || "Medium",
          onKeyDown: makeFilterKeyDownHandler(setMediumSearch),
        },
      },
      {
        accessorKey: "created_at",
        header: t.createdDate || "Created Date",
        enableSorting: true,
        enableColumnFilter: true,
        Filter: ({ column }) => (
          <SingleDateFilter
            column={column}
            translations={translations}
            onDateChange={(formatted) => {
              setCreatedDateSearch(formatted);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          />
        ),
        Cell: ({ cell }) => {
          const v = cell.getValue();
          return v ? dayjs(v).format("DD/MM/YYYY") : "-";
        },
        size: 200,
      },
      {
        accessorKey: "actions",
        header: t.action || "Action",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <button
            type="button"
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={t.viewAssessment || "View Assessment"}
          >
            <FaEye />
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, selectedId, translations],
  );

  return (
    <div>
      <div className={styles.actionsRow}>
        <CustomButton
          type="button"
          variant="contained"
          className={styles.addButton}
          fullWidth={false}
          onClick={handleAdd}
        >
          {t.add || "ADD"}
        </CustomButton>

        {/* DELETE button: enabled only when a single row checkbox is ticked */}
        <CustomButton
          type="button"
          variant="contained"
          className={styles.deleteButton}
          fullWidth={false}
          onClick={handleDeleteClick}
          disabled={!selectedId}
        >
          {t.delete || "DELETE"}
        </CustomButton>
      </div>

      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        enableColumnFilters={true}
        enableGlobalFilter={false}
        hideGlobalSearch={true}
      />

      {/* ==================== POPUP MODAL ==================== */}
      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={
          modalState.type === "success"
            ? translations?.labels?.success || t?.success || "Success"
            : modalState.type === "Confirm"
              ? translations?.labels?.delete || t?.delete || "DELETE"
              : translations?.labels?.failed || t?.failed || "Failed"
        }
        message={modalState.message}
        onClose={handleModalClose}
        onOk={modalState.type === "Confirm" ? handleConfirmDelete : undefined}
      />
    </div>
  );
};

export default AssessmentList;
